import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Menu,
  ChevronRight as ChevronRightIcon,
  Truck,
  Headphones,
  CreditCard,
  ThumbsUp,
  BadgeCheck,
  Play,
} from 'lucide-react';
import { fetchSliders, fetchCategoryTree, fetchAllCategoriesFlat } from '../utils/apiHelpers';
import type { Slider, Category } from '../utils/apiHelpers';
import { getCachedData, setCacheData } from '../utils/requestCache';
import { getImageUrl } from '../utils/imageHelper';

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  priceLabel?: string;
  price?: string;
  discountLabel?: string;
  image?: string;
}

const fallbackSlides: Slide[] = [
  {
    id: 1,
    title: 'The New Standard',
    subtitle: 'Under favorable 360 cameras',
    priceLabel: 'From',
    price: '$287',
    discountLabel: 'SALE 70%',
    image: '/hero-1.jpg',
  },
  {
    id: 2,
    title: 'Fresh Groceries',
    subtitle: 'Daily essentials delivered to your door',
    priceLabel: 'From',
    price: '$12',
    discountLabel: 'SALE 50%',
    image: '/hero-2.jpg',
  },
  {
    id: 3,
    title: 'Electronics & More',
    subtitle: 'Discover gadgets at unbeatable prices',
    priceLabel: 'From',
    price: '$99',
    discountLabel: 'NEW',
    image: '/hero-3.jpg',
  },
];

const sideBanners = [
  {
    id: 1,
    title: 'CATCH BIG DEALS ON THE CAMERAS',
    discountLabel: 'SALE 70%',
    gradient: 'from-[#fd9e10] via-[#ffb84d] to-[#ff9500]',
    image: '/laptop.png',
    to: '/shop?category=car-electronics&include_sub=true',
  },
  {
    id: 2,
    title: 'CATCH BIG DEALS ON THE CAMERAS',
    discountLabel: 'SALE 70%',
    gradient: 'from-[#ffb84d] via-[#fd9e10] to-[#ff9500]',
    image: '/camera.png',
    to: '/shop?category=car-electronics&include_sub=true',
  },
];

const serviceHighlights = [
  {
    icon: Truck,
    title: 'Free delivery',
    subtitle: 'Free Shipping for orders over $20',
  },
  {
    icon: Headphones,
    title: 'Support 24/7',
    subtitle: '24 hours a day, 7 days a week',
  },
  {
    icon: CreditCard,
    title: 'Payment',
    subtitle: 'Pay with Multiple Credit Cards',
  },
  {
    icon: ThumbsUp,
    title: 'Reliable',
    subtitle: 'Trusted by 2000+ major brands',
  },
  {
    icon: BadgeCheck,
    title: 'Guarantee',
    subtitle: 'Within 30 days for an exchange',
  },
];



const AUTO_PLAY_MS = 6000;

function mapSliderToSlide(s: Slider): Slide {
  return {
    id: s.id,
    title: s.title || 'The New Standard',
    subtitle: 'Under favorable 360 cameras',
    priceLabel: 'From',
    price: '$287',
    discountLabel: 'SALE 70%',
    image: getImageUrl(s.image),
  };
}

function resolveSlideImage(image?: string): string {
  if (!image) return '';
  if (image.startsWith('/') && !image.startsWith('//')) return image;
  return getImageUrl(image);
}

function flattenCategories(items: Category[]): Category[] {
  const flattened: Category[] = [];
  items.forEach((item) => {
    flattened.push(item);
    if (Array.isArray(item.children)) {
      flattened.push(...flattenCategories(item.children));
    }
  });
  return flattened;
}



export default function HeroSlider() {
  const [slides, setSlides] = useState<Slide[]>(() => {
    const cached = getCachedData<Slider[]>('sliders:all');
    if (cached && cached.length > 0) {
      return cached.map(mapSliderToSlide);
    }
    return fallbackSlides;
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [index, setIndex] = useState(0);
  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);
  const [hoveredPath, setHoveredPath] = useState<number[]>([]);
  const asideRef = useRef<HTMLElement | null>(null);
  const hoverClearTimer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSliders()
      .then((data: Slider[]) => {
        if (cancelled) return;
        if (data.length > 0) {
          setSlides(data.map(mapSliderToSlide));
          setCacheData('sliders:all', data, 24 * 60 * 60 * 1000);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSlides(fallbackSlides);
        }
      });

    // Try loading hierarchical category tree first. If empty or unavailable,
    // fall back to loading a flat list and build a tree client-side.
    const loadCategories = async () => {
      try {
        const list = await fetchCategoryTree();
        if (cancelled) return;
        if (Array.isArray(list) && list.length > 0) {
          setCategories(list);
          return;
        }
      } catch (e) {
        // ignore and try fallback
      }

      try {
        const flat = await fetchAllCategoriesFlat();
        if (cancelled) return;
        if (Array.isArray(flat) && flat.length > 0) {
          const byId = new Map<number, Category>();
          flat.forEach((c) => byId.set(c.id, { ...c, children: [] }));
          const roots: Category[] = [];
          byId.forEach((c) => {
            const pid = c.parent_id ?? c.parent ?? null;
            if (pid == null) roots.push(c);
            else {
              const parent = byId.get(pid as number);
              if (parent) parent.children = parent.children ?? [];
              parent?.children?.push(c);
            }
          });
          setCategories(roots);
          return;
        }
      } catch (e) {
        // final fallback below
      }

      if (!cancelled) setCategories([]);
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  // Close category submenu on any click (short delay allows link navigation)
  useEffect(() => {
    const onDocClick = () => {
      window.setTimeout(() => setHoveredPath([]), 50);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverClearTimer.current) {
        window.clearTimeout(hoverClearTimer.current);
        hoverClearTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, AUTO_PLAY_MS);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const current = slides[index];
  const backgroundImage = resolveSlideImage(current?.image);

  const goTo = (next: number) => {
    if (next < 0) next = slides.length - 1;
    if (next >= slides.length) next = 0;
    setIndex(next);
  };

  const isPathActive = (path: number[]) => path.every((id, idx) => hoveredPath[idx] === id);

  const renderCategoryItem = (cat: Category, path: number[]): JSX.Element => {
    const hasChildren = Array.isArray(cat.children) && cat.children.length > 0;
    const active = isPathActive(path);

    return (
      <li
        key={cat.id}
        className="relative border-b border-[var(--border-light)] last:border-b-0"
        onMouseEnter={() => {
          if (hoverClearTimer.current) {
            window.clearTimeout(hoverClearTimer.current);
            hoverClearTimer.current = null;
          }
          setHoveredPath(path);
        }}
      >
        <Link
          to={`/shop?category=${cat.id}&include_sub=true`}
          className={`flex items-center gap-3 px-4 py-3 text-sm text-[var(--foreground)] transition hover:bg-gray-50 hover:text-[var(--primary-red)]`}
        >
          <span className="flex-1">{cat.name}</span>
          {hasChildren && <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
        </Link>

        {hasChildren && active && (
          <div className="absolute left-full top-0 z-40 w-56 bg-white border border-[var(--border-light)] shadow-lg">
            <ul className="divide-y">
              {cat.children!.map((sub) => renderCategoryItem(sub, [...path, sub.id]))}
            </ul>
          </div>
        )}
      </li>
    );
  };

  return (
    <section className="bg-white px-4 py-4 md:px-6 md:py-5">
      <div className="mx-auto max-w-7xl animate-fade-in">
        {/* Hero grid: departments | main slider | side banners */}
        <div className="grid gap-4 overflow-hidden rounded-lg border border-[var(--border-light)] md:grid-cols-[240px_1fr] lg:grid-cols-[240px_1fr_260px]">
          {/* All Departments sidebar */}
          <aside
            ref={asideRef}
            className="hidden md:block"
            onMouseLeave={() => {
              if (hoverClearTimer.current) window.clearTimeout(hoverClearTimer.current);
              hoverClearTimer.current = window.setTimeout(() => setHoveredPath([]), 120);
            }}
          >
            <div className="flex items-center gap-2 bg-[var(--primary-red)] px-4 py-3.5 text-sm font-semibold text-white">
              <Menu className="h-4 w-4" />
              All Departments
            </div>
            <ul className="max-h-[420px]  bg-white">
              {categories.length > 0 ? (
                categories.map((cat) => renderCategoryItem(cat, [cat.id]))
              ) : (
                <li className="px-4 py-3 text-sm text-[var(--text-muted)]">Loading categories...</li>
              )}
            </ul>
          </aside>

          {/* Main slider */}
          <div className="relative rounded-lg min-h-[220px] bg-[#1a1a2e] text-white sm:min-h-[280px] lg:min-h-[420px]">
            {backgroundImage && (
              <div
                className="absolute inset-0 rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${backgroundImage})` }}
              />
            )}
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

            <div className="relative flex h-full min-h-[220px] flex-col justify-end px-6 py-8 sm:min-h-[280px] sm:px-10 lg:min-h-[420px]">
              <Link
                to="/shop"
                className="mt-auto inline-flex w-fit items-center gap-2 text-sm font-semibold text-white transition hover:opacity-90"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/60">
                  <Play className="h-3 w-3 fill-white" />
                </span>
                Shop Now
              </Link>
            </div>

            <button
              type="button"
              onClick={() => goTo(index - 1)}
              className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 transition hover:bg-black/60"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 transition hover:bg-black/60 lg:right-3"
              aria-label="Next slide"
            >
              <ChevronRight className="h-4 w-4" />
            </button>

            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
              {slides.map((slide, i) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goTo(i)}
                  className={`h-2 w-2 rounded-full transition ${
                    i === index ? 'bg-white' : 'bg-white/40'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Side banners */}
          <div className="hidden md:grid md:col-span-2 md:grid-cols-2 gap-4 lg:col-span-1 lg:grid-cols-1">
            {sideBanners.map((banner, idx) => (
              <Link
                key={banner.id}
                to={banner.to}
                className={`group relative flex overflow-hidden rounded-lg bg-gradient-to-br ${banner.gradient} p-5 text-white transition hover:opacity-95 shadow-2xl ${idx === 0 ? 'border-b border-[var(--border-light)] md:border-b-0' : ''}`}
              >
                {/* Radial light overlay */}
                <div
                  className="pointer-events-none absolute inset-0 opacity-30"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.15) 0%, transparent 60%)',
                  }}
                />
                {/* Shadow line decoration top */}
                <div className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                {/* Shadow line decoration bottom */}
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                {/* Side accent line left */}
                <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-white/40 via-white/10 to-transparent" />
                {/* Decorative circles */}
                <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-white/5 blur-2xl" />
                <div className="pointer-events-none absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/5 blur-2xl" />
                <div className="relative z-20 flex w-full items-center gap-4">
                  <div className="flex-1 space-y-4">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-black text-[9px] font-bold leading-tight">
                      {banner.discountLabel.split(' ').map((word, i) => (
                        <span key={i}>{word}</span>
                      ))}
                    </div>
                    <p className="text-xs font-bold uppercase leading-snug tracking-wide">
                      {banner.title}
                    </p>
                    <span className="inline-flex items-center gap-2 text-xs font-semibold">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-white/60">
                        <Play className="h-2.5 w-2.5 fill-white" />
                      </span>
                      Shop Now
                    </span>
                  </div>
                  {banner.image && (
                    <div className="relative h-48 w-2/3 overflow-hidden rounded-3xl scale-[1.07]">
                      <img
                        src={banner.image}
                        alt={banner.title}
                        className="h-full w-full object-contain object-right transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Mobile departments strip */}
        <div className="mt-3 hidden overflow-x-auto scrollbar-hide lg:hidden">
          <div className="flex gap-2 pb-1">
            <Link
              to="/shop"
              className="shrink-0 rounded-full bg-[var(--primary-red)] px-4 py-2 text-xs font-semibold text-white"
            >
              All Departments
            </Link>
            {flatCategories.map((cat) => {
              const to = `/shop?category=${cat.id}&include_sub=true`;
              return (
                <Link
                  key={cat.id}
                  to={to}
                  className="shrink-0 rounded-full border border-[var(--border-light)] bg-white px-4 py-2 text-xs font-medium text-[var(--foreground)]"
                >
                  {cat.name}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Service highlights */}
        <div className="mt-4 hidden grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 md:grid">
          {serviceHighlights.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="flex items-center gap-3 rounded-lg border border-[var(--border-light)] bg-white px-4 py-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--border-light)] text-[var(--text-muted)]">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--foreground)]">{item.title}</p>
                  <p className="text-xs text-[var(--text-muted)]">{item.subtitle}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* <div className="mt-6"> */}
          {/* <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"> */}
            {/* <div> */}
              {/* <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[var(--primary-red)]"> */}
                {/* Hot Picks */}
              {/* </p> */}
              {/* <h2 className="text-2xl font-bold text-[var(--foreground)]"> */}
                {/* Best Featured Products */}
              {/* </h2> */}
            {/* </div> */}
            {/* <Link */}
              {/* // to="/shop" */}
              {/* // className="text-sm font-medium text-[var(--primary-red)] hover:underline" */}
            {/* // > */}
              {/* View All */}
            {/* </Link> */}
          {/* </div> */}
          {/* <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"> */}
            {/* {loadingFeatured */}
              {/* // ? Array.from({ length: 4 }).map((_, idx) => ( */}
                  {/* <div
                    // key={idx}
                    // className="rounded-3xl border border-[var(--border-light)] bg-white p-4 shadow-sm"
                  // >
                    {/* <div className="h-56 rounded-3xl bg-gray-100 animate-pulse" /> */}
                    {/* <div className="mt-4 space-y-3"> */}
                      {/* <div className="h-4 w-5/6 rounded bg-gray-200 animate-pulse" /> */}
                      {/* <div className="h-4 w-full rounded bg-gray-200 animate-pulse" /> */}
                      {/* <div className="h-10 rounded bg-gray-200 animate-pulse" /> */}
                    {/* </div> */}
                  {/* </div> */}
                {/* // )) */}
              {/* // : featuredProducts.map((product) => ( */}
                  {/* // <ProductCard key={product.id} product={product} /> */}
                {/* // ))} */}
          {/* </div> */}
        {/* </div> */}
      </div>
    </section>
  );
}
